import React, { useEffect, useState } from "react";
import { Box, Flex } from "rebass";
import { Button } from "rebass/styled-components";
import { Link, useLocation } from "react-router-dom";
import styled, { css } from "styled-components";

import { NAV_LINKS, isActive } from "../utils";
import {
  navLinkActiveFill,
  NavLinkText,
  StyledBiCompass,
  StyledBiCompassFilled,
  StyledBiDotsHorizontalRounded,
  StyledBiUser,
  StyledBiUserFilled,
  StyledBiPalette,
  StyledBiTrophy,
  StyledBiVideo,
  StyledBiEdit,
  StyledDivider,
  StyledHistoryLogo,
  StyledTeamsLogo,
  StyledTeamsLogoFilled,
  StyledMdOutlineVideoLibrary,
  StyledMdVideoLibrary,
} from "../styledComponents";
import { Tooltip } from "../../Tooltip";
import { useSetRecoilState } from "recoil";

import { useUser } from "../../../Hooks/useUser";
import { useProEditAccess } from "../../../Hooks/useProEditAccess";
import { useIsInternalAccount } from "../../../Hooks/useIsInternalAccount";
import { useShouldShowSubscriptionOverhaul } from "../../../Hooks/useSubscriptionOverhaul";
import { useUnsavedDraftCount } from "../../../Hooks/useUnsavedDraftCount";
import { usePortal } from "../../../Hooks/usePortal";
import { PartnerTypeModal } from "../../Modals/PartnerType";
import { TimelineEditorInfoModal } from "../../Modals/TimelineEditorInfo";
import { AllstarModalState } from "../../../State/modals";
import { ModalType } from "../../../@types";
import { analytics } from "MVVM/Analytics";

const SidebarCollapsed = () => {
  const { allstarUser } = useUser();
  const { hasProEditAccess } = useProEditAccess();
  const isInternalAccount = useIsInternalAccount();
  const showSubscriptionOverhaul = useShouldShowSubscriptionOverhaul();
  const unsavedDraftCount = useUnsavedDraftCount();
  const location = useLocation();
  const [libraryVisited, setLibraryVisited] = useState(false);

  useEffect(() => {
    if (location.pathname === "/library") {
      setLibraryVisited(true);
    }
  }, [location.pathname]);

  const showDraftIndicator = unsavedDraftCount && !libraryVisited;

  const {
    portalOpen: timelinePortalOpen,
    setPortalOpen: setTimelinePortalOpen,
    Portal: TimelinePortal,
  } = usePortal(<TimelineEditorInfoModal />);
  const username = allstarUser?.user?.profile?.username;
  const profileLink = username
    ? `${NAV_LINKS.profile}/${username}`
    : NAV_LINKS.login;

  return (
    <SidebarContainerCollapsed>
      {timelinePortalOpen && TimelinePortal}
      <Flex flexDirection="column" alignItems="center" style={{ gap: 8 }}>
        <NavLink to={NAV_LINKS.root} isActive={isActive([NAV_LINKS.root])}>
          {isActive([NAV_LINKS.root]) ? (
            <StyledBiCompassFilled />
          ) : (
            <StyledBiCompass />
          )}
        </NavLink>
        <NavLink
          to={profileLink}
          isActive={isActive(["/u", NAV_LINKS.profile])}
        >
          {isActive(["/u", NAV_LINKS.profile]) ? (
            <StyledBiUserFilled />
          ) : (
            <StyledBiUser />
          )}
        </NavLink>
        {allstarUser.loggedIn && showSubscriptionOverhaul && (
          <LibraryNavWrapper>
            <NavLink
              to={NAV_LINKS.library}
              isActive={isActive([NAV_LINKS.library])}
            >
              {isActive([NAV_LINKS.library]) ? (
                <StyledMdVideoLibrary />
              ) : (
                <StyledMdOutlineVideoLibrary />
              )}
            </NavLink>
            {showDraftIndicator && <DraftDot />}
          </LibraryNavWrapper>
        )}
        <TeamsNavLink
          to={NAV_LINKS.teams}
          isActive={isActive(["/teams", NAV_LINKS.teams, "/team"])}
          onClick={() => {
            analytics.track("Button - Click", {
              locationIdentifier: "teams_collapsed",
              location: "left_nav",
            });
          }}
        >
          {isActive(["/teams", NAV_LINKS.teams, "/team"]) ? (
            <StyledTeamsLogoFilled />
          ) : (
            <StyledTeamsLogo />
          )}
          <StyledNewBadge>New</StyledNewBadge>
        </TeamsNavLink>
        <StyledDivider />
        <NavLink
          to={NAV_LINKS.matchHistory}
          isActive={isActive([NAV_LINKS.matchHistory])}
        >
          <StyledHistoryLogo />
        </NavLink>
        <NavLink to={NAV_LINKS.studio} isActive={false}>
          <StyledBiPalette />
        </NavLink>
        <NavLink
          to={NAV_LINKS.montages}
          isActive={isActive([NAV_LINKS.montages])}
        >
          <StyledBiVideo />
        </NavLink>
        {hasProEditAccess && (
          <TimelineNavLink
            to="#"
            isActive={false}
            onClick={() => {
              analytics.track("Button - Click", {
                locationIdentifier: "timeline_editor_collapsed",
                location: "left_nav",
              });
              setTimelinePortalOpen(true);
            }}
          >
            <StyledBiEdit />
            <StyledBetaBadge>Beta</StyledBetaBadge>
          </TimelineNavLink>
        )}
        <StyledDivider />
        {isInternalAccount && (
          <>
            <NavItem>
              <NavLink
                to="/competitions"
                isActive={isActive(["/competitions"])}
              >
                <StyledBiTrophy />
              </NavLink>
            </NavItem>
            <StyledDivider />
          </>
        )}
        <StyledTooltip
          type="click"
          tooltipContent={<SubNav />}
          position={{
            top: "calc(100% - 80px)",
            left: "64px",
          }}
        >
          <StyledBiDotsHorizontalRounded />
        </StyledTooltip>
      </Flex>
    </SidebarContainerCollapsed>
  );
};

const SubNav = () => {
  const { allstarUser } = useUser();
  const setAllstarModalState = useSetRecoilState(AllstarModalState);
  const { portalOpen, setPortalOpen, Portal } = usePortal(<PartnerTypeModal />);

  const handleSupportClick = (e: React.MouseEvent) => {
    const userGroup = allstarUser?.user?.userGroup?.type;
    if (!allstarUser.loggedIn || !userGroup || userGroup === "FREE") {
      e.preventDefault();
      setAllstarModalState({
        data: {},
        functions: {},
        isOpen: ModalType.DiscordSupport,
      });
    } else {
      window.location.replace(NAV_LINKS.support);
    }
  };

  return (
    <Flex
      flexDirection="column"
      alignItems="flex-start"
      justifyContent="flex-start"
      style={{ gap: 8 }}
    >
      {portalOpen && Portal}
      {allstarUser.loggedIn && (
        <NavItem>
          <SubNavLink to={NAV_LINKS.upgrade} isActive={false}>
            <NavLinkText>Upgrade</NavLinkText>
          </SubNavLink>
        </NavItem>
      )}
      <NavItem>
        <SubNavLink to="#" onClick={() => setPortalOpen(true)} isActive={false}>
          <NavLinkText>For Partners</NavLinkText>
        </SubNavLink>
      </NavItem>
      <NavItem>
        <SubNavLink to="#" onClick={handleSupportClick} isActive={false}>
          <NavLinkText>Support</NavLinkText>
        </SubNavLink>
      </NavItem>
      <NavItem>
        <SubNavLink to="/start" isActive={isActive(["/start"])}>
          <NavLinkText>How Allstar Works</NavLinkText>
        </SubNavLink>
      </NavItem>
    </Flex>
  );
};

const NavItem = styled(Flex)`
  color: ${({ theme }) => theme.colors.chalk} !important;
  text-decoration: none;
  width: 100%;
`;

const SidebarContainerCollapsed = styled(Box)`
  background-color: ${({ theme }) => theme.colors.navy600};
  border-right: 1px solid ${({ theme }) => theme.colors.navy400};
  padding: 16px;
  position: relative;
  width: 84px;

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    display: none;
  }
`;

const navLinkStyle = css<{ isActive: boolean }>`
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: 12px 0;
  width: 100%;
`;

const StyledTooltip = styled(Tooltip)`
  ${navLinkStyle}

  :hover {
    background: ${({ theme }) => theme.colors.envyAlpha10};

    svg * {
      color: ${({ theme }) => theme.colors.envy};
    }
  }
`;

const NavLink = styled(Link)`
  ${navLinkStyle}
  text-decoration: none;
  padding: 12px;

  ${(props) => (props.isActive ? navLinkActiveFill : "")}

  :hover {
    ${navLinkActiveFill}
  }
`;

const SubNavLink = styled(NavLink)`
  text-decoration: none;
  justify-content: flex-start;
`;

const StyledBetaBadge = styled(Button)`
  align-items: center;
  background: linear-gradient(227.35deg, #72439a 6.85%, #f95370 89.04%);
  border-radius: 100px;
  color: ${({ theme }) => theme.colors.ice} !important;
  display: inline-flex;
  font-size: 10px;
  font-weight: 600;
  height: 20px;
  justify-content: center;
  line-height: 1;
  padding: 0;
  text-transform: uppercase;
  width: 40px;
`;

const StyledNewBadge = styled(Button)`
  background: ${({ theme }) => theme.colors.envy};
  border-radius: 100px;
  color: ${({ theme }) => theme.colors.ice} !important;
  font-size: 10px;
  font-weight: 600;
  height: 20px;
  line-height: 12px;
  padding: 0px;
  text-transform: uppercase;
  width: 40px;
`;

const TeamsNavLink = styled(NavLink)`
  flex-direction: column;
  align-items: center;
  gap: 5px;
`;

const TimelineNavLink = styled(NavLink)`
  align-items: center;
  flex-direction: column;
  gap: 5px;
`;

const LibraryNavWrapper = styled(Flex)`
  position: relative;
  width: 100%;
`;

const DraftDot = styled(Box)`
  background: ${({ theme }) => theme.colors.alert};
  border: 2px solid ${({ theme }) => theme.colors.navy600};
  border-radius: 50%;
  height: 10px;
  pointer-events: none;
  position: absolute;
  right: 6px;
  top: 6px;
  width: 10px;
`;

export { SidebarCollapsed };
